import type { MutableRefObject, ReactElement } from 'react'
import { useEffect, useImperativeHandle, useState } from 'react'

import type { WhiteLabel } from '@skytech/types'
import { useCombobox } from 'downshift'
import CloseIcon from 'mdi-react/CloseIcon'

import { Button } from '../Button'
import { ErrorMessage } from '../ErrorMessage'
import { Label } from '../Label'

import './styles.scss'

export type ComboboxRef = { onResetCombobox: () => void }

export type ComboboxProps<T> = {
  id: string
  list: T[]
  ariaLabel: string
  isFullWidth?: boolean
  className?: string
  wrapperClassName?: string
  label?: string | ReactElement
  value?: string | number
  comboboxMethodsRef?: MutableRefObject<ComboboxRef | null>
  type?: string
  error?: string
  customError?: string
  touched?: boolean
  placeholder?: string
  marginBottom?: number
  marginRight?: number
  minWidth?: number
  capitalizeLabel?: boolean
  RightElement?: ReactElement
  hitsTranslation?: string | ReactElement
  noResultsFoundTranslation?: string | ReactElement
  isDisabled?: boolean
  onSetFieldTouched: () => void
  stringMatcher: (item: T | null) => string
  onSetFieldValue?: (value: string) => void
  onCustomChange?: () => void
}

export const Combobox = <T,>({
  id,
  list,
  ariaLabel,
  value,
  comboboxMethodsRef,
  placeholder,
  className = '',
  wrapperClassName = '',
  label,
  type,
  error,
  customError,
  RightElement,
  marginBottom,
  marginRight,
  capitalizeLabel,
  touched,
  minWidth,
  hitsTranslation,
  noResultsFoundTranslation,
  isFullWidth,
  isDisabled,
  onSetFieldValue,
  onSetFieldTouched,
  stringMatcher,
  onCustomChange,
}: ComboboxProps<T>) => {
  const [filteredItems, setFilteredItems] = useState(() => {
    if (!String(value)) return list
    return list.filter(item =>
      stringMatcher(item).toLowerCase().includes(String(value).toLowerCase())
    )
  })

  useEffect(() => {
    let filteredList = list
    if (String(value))
      filteredList = list.filter(item =>
        stringMatcher(item).toLowerCase().includes(String(value).toLowerCase())
      )
    setFilteredItems(filteredList)
  }, [list, stringMatcher, value])

  const {
    selectItem,
    isOpen,
    getMenuProps,
    getInputProps,
    highlightedIndex,
    getItemProps,
    selectedItem,
    reset,
  } = useCombobox({
    items: filteredItems,
    initialInputValue: String(value),
    initialSelectedItem: list.find(item => stringMatcher(item) === String(value)),
    onInputValueChange({ inputValue }) {
      const filteredItems = list.filter(item =>
        stringMatcher(item)
          .toLowerCase()
          .includes((inputValue || '').toLowerCase())
      )
      setFilteredItems(filteredItems)
      onSetFieldValue?.(inputValue ?? '')
    },
    onSelectedItemChange() {
      onCustomChange?.()
    },
    itemToString: stringMatcher,
  })

  useImperativeHandle(comboboxMethodsRef, () => ({
    onResetCombobox: reset,
  }))

  const errorClass = error && touched ? 'has-error' : ''
  const fullWidthClass = isFullWidth ? 'is-full-width' : ''
  const whiteLabel = localStorage.getItem('org') as WhiteLabel

  const visibleResults = filteredItems?.slice(0, 10)
  const remainingResults = (filteredItems?.length || 0) - (visibleResults?.length || 0)

  return (
    <div
      className={`ui-combobox-wrapper ${wrapperClassName} ${isDisabled ? 'is-disabled' : ''}`}
      style={{ marginBottom: marginBottom || 0, marginRight: marginRight || 0, minWidth }}
    >
      {label && (
        <Label capitalizeLabel={capitalizeLabel} htmlFor={id}>
          {label}
        </Label>
      )}

      <div className="ui-combobox-input-wrapper">
        <input
          {...getInputProps({
            id,
            className: `ui-combobox ${errorClass} ${className} ${fullWidthClass} ${whiteLabel}`,
            type,
            placeholder,
            disabled: isDisabled,
            'aria-label': ariaLabel,
          })}
        />

        {Boolean(value) && !isDisabled && (
          <Button
            className="absolute bottom-1/2 right-10 m-0 translate-y-1/2 transform p-0"
            size="extraSmall"
            variant="tertiary"
            aria-label="clear selection"
            Icon={<CloseIcon />}
            onClick={() => {
              selectItem(null)
              onSetFieldValue?.('')
              onSetFieldTouched()
              onCustomChange?.()
            }}
          />
        )}
      </div>

      <ul {...getMenuProps()} className="menu">
        {isOpen && (
          <>
            {filteredItems.length === 0 ? (
              <li className="menu-item">
                <span>{noResultsFoundTranslation || 'No results found'}</span>
              </li>
            ) : (
              <>
                {visibleResults.map((item, index) => (
                  <li
                    className={`menu-item ${whiteLabel} ${
                      highlightedIndex === index ? 'highlighted' : ''
                    } ${selectedItem === item ? 'selected' : ''}`}
                    key={stringMatcher(item) + index}
                    {...getItemProps({ item, index })}
                  >
                    <span>{stringMatcher(item)}</span>
                  </li>
                ))}

                {remainingResults > 0 && (
                  <li className="menu-item hits">
                    <span>
                      + {remainingResults} {hitsTranslation || 'hit(s)'}
                    </span>
                  </li>
                )}
              </>
            )}
          </>
        )}
      </ul>

      {RightElement && <div className="right-element">{RightElement}</div>}

      {error && touched && <ErrorMessage>{error}</ErrorMessage>}
      {customError && <ErrorMessage>{customError}</ErrorMessage>}
    </div>
  )
}
